// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { spacing, typography } from "@recidiviz/design-system";
import { format, isEqual } from "date-fns";
import { rem } from "polished";
import type { JSX } from "react";
import MarkdownView from "react-showdown";
import styled from "styled-components";

import { palette } from "~design-system";

import { Denial, Submission } from "../../FirestoreStore";
import { appendDateSuffixIfMissing } from "../../utils";
import { ActedOnTextAddition, Opportunity } from "../../WorkflowsStore";
import { reasonsIncludesOtherKey } from "../utils/workflowsUtils";
import { DetailsShadedBox } from "./styles";
import { formatSupervisionEndDatePhrase } from "./utils";

const MarkedIneligibleReasonsText = styled(DetailsShadedBox)`
  ${typography.Sans14}
  color: ${palette.pine1};
  margin: 1.5rem 0;
`;

const DenialReasonText = styled.pre`
  ${typography.Sans14}
  color: ${palette.slate85};
  text-wrap: inherit;
  margin: ${rem(spacing.md)} 0 0 0;
`;

const StyledMarkdownView = styled(MarkdownView)`
  p {
    margin: 1rem 0 0;
  }
  a {
    color: ${palette.signal.links} !important;
    padding-top: ${rem(spacing.sm)};
    &:hover {
      text-decoration: underline;
    }
  }
`;

export function buildActedOnText({
  denial,
  isSubmitted,
  deniedTabTitle,
  snoozedOnDate,
  snoozedBy,
  submittedTabTitle,
  submittedUpdate,
  subcategoryCopy,
  actedOnTextAddition,
  isIndefinitelySnoozed,
}: {
  denial?: Denial;
  isSubmitted: boolean;
  deniedTabTitle: string;
  snoozedOnDate?: Date;
  snoozedBy?: string;
  submittedTabTitle: string;
  submittedUpdate?: Submission;
  subcategoryCopy?: string;
  actedOnTextAddition?: ActedOnTextAddition;
  isIndefinitelySnoozed: boolean;
}): string | undefined {
  if (!isSubmitted && !denial) return;

  const status = denial ? deniedTabTitle : submittedTabTitle;
  const actionBy = denial ? snoozedBy : submittedUpdate?.by;
  const actionDate = denial ? snoozedOnDate : submittedUpdate?.date.toDate();

  if (!actionBy || !actionDate) return;

  const subcategorySubstr = subcategoryCopy ? `: ${subcategoryCopy}` : "";

  let textAddition = undefined;
  if (actedOnTextAddition) {
    if (denial && actedOnTextAddition.DENIED) {
      textAddition = actedOnTextAddition.DENIED;
    } else if (isSubmitted && actedOnTextAddition.SUBMITTED) {
      textAddition = actedOnTextAddition.SUBMITTED;
    }
  }

  let actionText: string;
  if (isIndefinitelySnoozed) {
    actionText = "Marked indefinitely ineligible";
  } else {
    actionText = `${status}${subcategorySubstr}`;
  }

  return `${actionText} by ${actionBy} on ${format(
    actionDate,
    "LLLL d, yyyy",
  )}${textAddition ?? "."}`;
}

export function getusAzTprDtpAdditionalInformation(
  opportunity: Opportunity,
): JSX.Element | undefined {
  const usAzTprDtpAdditionalInformation = `Thank you for flagging an error in this person's TPR date in ACIS. Recidiviz will automatically report this information to Central Time Comp. If you have questions for their team, you're encouraged to reach out to them directly via email.`;
  if (["usAzReleaseToDTP", "usAzReleaseToTPR"].includes(opportunity.type)) {
    return (
      <>
        <div>{usAzTprDtpAdditionalInformation}</div>
        <br />
      </>
    );
  }
}

export function buildResurfaceText(
  opportunity: Opportunity,
  snoozeUntil: Date | undefined,
  isIndefiniteReason: boolean,
  labels: { releaseDateCopy: string; supervisionEndDateCopy: string },
): string | undefined {
  const { person } = opportunity;
  if (isIndefiniteReason)
    return `${person.displayPreferredName} will no longer be surfaced for this opportunity`;
  if (!snoozeUntil) return;
  const { supervisionEndDateCopy, releaseDateCopy } = labels;
  const dateStr = format(snoozeUntil, "LLLL d, yyyy");

  let endDateString;

  if (
    "expirationDate" in person &&
    person.expirationDate instanceof Date &&
    isEqual(snoozeUntil, person.expirationDate)
  ) {
    endDateString = `${dateStr} is ${person.displayPreferredName}'s ${formatSupervisionEndDatePhrase(supervisionEndDateCopy)}.`;
  } else if (
    "releaseDate" in person &&
    person.releaseDate instanceof Date &&
    isEqual(snoozeUntil, person.releaseDate)
  ) {
    endDateString = `${dateStr} is ${person.displayPreferredName}'s ${appendDateSuffixIfMissing(releaseDateCopy)}.`;
  } else {
    endDateString = `${person.displayPreferredName} may be surfaced again on or after ${dateStr}.`;
  }

  if (opportunity.generateCaseNoteText) {
    return opportunity.generateCaseNoteText;
  }

  return endDateString;
}

export function buildDenialReasonsListText(
  opportunity: Opportunity,
  denialReasons: string[],
): string {
  const denialReasonsList = denialReasons.join(", ");
  const ineligibleReasonsListCopy = opportunity.config.isAlert
    ? "Override reasons:"
    : "Not eligible reasons:";

  return `${ineligibleReasonsListCopy}${" "}${denialReasonsList}`;
}

export function buildActedOnTextAndResurfaceText(
  opportunity: Opportunity,
  snoozeUntil: Date | undefined,
  labels: { releaseDateCopy: string; supervisionEndDateCopy: string },
) {
  const actedOnText = buildActedOnText(opportunity);
  const resurfaceText = buildResurfaceText(
    opportunity,
    snoozeUntil,
    opportunity.isIndefinitelySnoozed,
    labels,
  );
  return [actedOnText, resurfaceText];
}

// Filtering out admin panel formatting for checkboxes
// ie: Other: Other reasons here
const stripCode = (value: string) => {
  if (value.includes(":")) {
    const val = value.split(":")[1].trim();
    return val.charAt(0).toUpperCase() + val.slice(1);
  } else return value;
};

const IneligibleReasonList: React.FC<{
  opportunity: Opportunity;
  denialReasons: string[];
}> = ({ opportunity, denialReasons }) => {
  const denialReasonsMap = opportunity.denialReasons;

  return (
    <div data-testid="IneligibleReasonsList">
      <div>
        {opportunity.config.isAlert
          ? "Override reasons:"
          : "Not eligible reasons:"}
      </div>
      <ul>
        {denialReasons.map((code) => (
          <li key={code}>
            {code in denialReasonsMap
              ? `${code} - ${stripCode(denialReasonsMap[code])}`
              : code}
          </li>
        ))}
      </ul>
    </div>
  );
};

const MarkedIneligibleReasons: React.FC<{
  opportunity: Opportunity;
  actedOnTextAndResurfaceTextPair: ReturnType<
    typeof buildActedOnTextAndResurfaceText
  >;
  denialReasons?: string[];
}> = ({ opportunity, actedOnTextAndResurfaceTextPair, denialReasons }) => {
  if (opportunity.isSubmitted) {
    return (
      <MarkedIneligibleReasonsText className="MarkedIneligibleReasonsText">
        <div>{`Marked as ${actedOnTextAndResurfaceTextPair[0]}`}</div>
        {opportunity.config.customSubmittedText && (
          <StyledMarkdownView
            markdown={opportunity.config.customSubmittedText}
            options={{ openLinksInNewWindow: true }}
          ></StyledMarkdownView>
        )}
      </MarkedIneligibleReasonsText>
    );
  }

  if (!denialReasons) return null;

  const actedOnTextAndResurfaceText = actedOnTextAndResurfaceTextPair.every(
    (text) => text,
  )
    ? actedOnTextAndResurfaceTextPair.join(" ")
    : undefined;

  const denialReasonsWithInput = denialReasons.filter(
    (x) =>
      (opportunity.denial?.userInput && x in opportunity.denial.userInput) ||
      reasonsIncludesOtherKey([x]),
  );

  return (
    <MarkedIneligibleReasonsText className="MarkedIneligibleReasonsText">
      {actedOnTextAndResurfaceText && (
        <>
          {" "}
          <div>{actedOnTextAndResurfaceText} </div> <br />
          {getusAzTprDtpAdditionalInformation(opportunity)}
        </>
      )}
      {opportunity.denial && (
        <div>
          <IneligibleReasonList
            opportunity={opportunity}
            denialReasons={denialReasons}
          />
          {denialReasonsWithInput.map((code) => {
            let value = "";
            if (!opportunity.denial) return;
            if (reasonsIncludesOtherKey([code])) {
              value = `${code}: ${opportunity.denial.otherReason}`;
            } else if (opportunity.denial.userInput) {
              value = `${code}: ${opportunity.config.denialInputSettings[code].prefix ?? ""}${opportunity.denial.userInput[code]}`;
            }
            return <DenialReasonText>&quot;{value}&quot;</DenialReasonText>;
          })}
        </div>
      )}
    </MarkedIneligibleReasonsText>
  );
};
export default MarkedIneligibleReasons;
